import { useEffect, useRef } from 'react';
import type { MountOptions } from '../../lib/motion/core';

type Props = MountOptions & { className?: string };

type IdleWindow = Window & {
  requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
  cancelIdleCallback?: (id: number) => void;
};

/**
 * The animated background, mounted after first paint in its own lazy chunk, so it never
 * competes with the page's text for LCP. The element's own CSS background is the static
 * design underneath — it is what visitors see before the canvas fades in, when Canvas is
 * unavailable, and (as a still frame) with prefers-reduced-motion.
 */
export default function MotionBackground({ className, variant, palette, intensity, interactive }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const mountedOnce = useRef(false);
  const key = [variant, palette.ink, palette.accent, palette.accent2, palette.accent3 ?? '', intensity ?? 1, interactive !== false].join('|');

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    let handle: { destroy(): void } | null = null;
    let cancelled = false;
    const start = () => {
      import('../../lib/motion')
        .then(({ mountBackground }) => {
          if (!cancelled) handle = mountBackground(el, { variant, palette, intensity, interactive });
        })
        .catch(() => {
          /* the CSS backdrop stays */
        });
    };
    const w = window as IdleWindow;
    // First mount waits for idle (never competes with LCP); a later palette change swaps at once.
    const idle = !mountedOnce.current && Boolean(w.requestIdleCallback);
    const id = idle ? w.requestIdleCallback!(start, { timeout: 1500 }) : window.setTimeout(start, mountedOnce.current ? 0 : 250);
    mountedOnce.current = true;
    return () => {
      cancelled = true;
      if (idle) w.cancelIdleCallback?.(id);
      else window.clearTimeout(id);
      // Keep the old canvas a moment so a palette or theme change cross-fades instead of flashing.
      const old = handle;
      if (old) window.setTimeout(() => old.destroy(), 700);
    };
    // `key` covers every option; the object props are new on each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return <div ref={ref} aria-hidden="true" className={className} />;
}
